import type { ModuleInfo } from '../Bridge';
import { Bridge } from '../Bridge';
import { once } from '../utils';
import type { Version } from '../Version';

export type ProductsParams = {
    ids: string[];
};

export type Product = {
    id: string;
    displayName: string;
    displayPrice: string;
    description: string;
    price: string;
    currencyCode: string;
};

export type PurchaseParams = {
    id: string;
    quantity?: number;
    payload?: string;
};

export type VerificationResult =
    | {
          type: 'verified';
          transaction: Transaction;
          receipt: string;
      }
    | {
          type: 'unverified';
          transaction: Transaction;
          error: string;
      };

export type PurchaseResponse =
    | VerificationResult
    | { type: 'not found' }
    | { type: 'user cancelled' }
    | { type: 'unknown' };

export type UnfinishedResponse = VerificationResult[];

export type Transaction = {
    id: string;
    appBundleID: string;
    productID: string;
    purchaseDate: string;
    purchaseQuantity: number;
};

export type FinishParams = {
    id: string;
};

export type FinishResponse = { type: 'success' } | { type: 'failure' };

export class PurchasesModule {
    constructor(
        readonly bridge: Bridge,
        readonly module: ModuleInfo<'purchases', 0>,
    ) {}

    private request<T>(data: any): Promise<T> {
        return this.bridge.request(this.module.id, data);
    }

    /**
     * Since purchases@0.0.0
     */
    async products(params: ProductsParams) {
        const response: Product[] = await this.request({
            type: 'products',
            ...params,
        });
        return response;
    }

    /**
     * Since purchases@0.0.0
     */
    async unfinished() {
        const response: UnfinishedResponse = await this.request({
            type: 'unfinished',
        });
        return response;
    }

    /**
     * Since purchases@0.0.0
     */
    async purchase(params: PurchaseParams) {
        const response: PurchaseResponse = await this.request({
            type: 'purchase',
            ...params,
        });
        return response;
    }

    /**
     * Since purchases@0.0.0
     */
    async finish(params: FinishParams) {
        const response: FinishResponse = await this.request({
            type: 'finish',
            ...params,
        });
        return response;
    }
}

/**
 * The Purchases module provides methods to purchase products.
 *
 * Get an instance of this module using `Purchases.getInstance()`.
 */
export class Purchases {
    /**
     * Creates a new instance using the provided bridge.
     */
    static create = (bridge: Bridge) => {
        const module = bridge.findModule('purchases', 0);
        if (module) {
            return new Purchases(new PurchasesModule(bridge, module), module.version);
        }
    };

    /**
     * Returns a shared instance.
     */
    static getInstance = once(() => {
        const bridge = Bridge.getInstance();
        if (bridge) {
            return Purchases.create(bridge);
        }
    });

    /** Returns the list of products. */
    readonly products: (params: ProductsParams) => Promise<Product[]>;

    /** Returns the list of unfinished transactions. */
    readonly unfinished: () => Promise<UnfinishedResponse>;

    /** Purchases a product. */
    readonly purchase: (params: PurchaseParams) => Promise<PurchaseResponse>;

    /** Finishes a transaction. */
    readonly finish: (params: FinishParams) => Promise<FinishResponse>;

    constructor(
        readonly module: PurchasesModule,
        readonly version: Version<0>,
    ) {
        this.products = module.products.bind(module);
        this.unfinished = module.unfinished.bind(module);
        this.purchase = module.purchase.bind(module);
        this.finish = module.finish.bind(module);
    }
}
